import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";
import { CalendarDays, Layers, Users, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function PublicCourse() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/courses/${slug}`);
        setC(data);
      } catch { toast.error("Course not found"); }
    })();
  }, [slug]);

  if (!c) return <div className="min-h-screen flex items-center justify-center text-[#4A4D57]">Loading…</div>;
  const isPaid = (c.price || 0) > 0;

  return (
    <div className="min-h-screen vp-hero-bg">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {c.host?.brand_logo ? <img src={c.host.brand_logo} alt="brand" className="w-9 h-9 rounded-xl object-cover" /> :
            <div className="w-9 h-9 rounded-xl bg-white border border-[#0B5CFF]/40" />}
          <span className="font-display font-semibold">{c.host?.name}</span>
        </div>
        <button onClick={() => setOpen(true)} className="vp-btn-primary text-sm">
          Enroll {isPaid ? `· ${fmtINR(c.price)}` : "Free"}
        </button>
      </div>

      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-6 pb-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 vp-fade-up">
          <div className="vp-chip mb-5"><Layers className="w-3.5 h-3.5 text-[#0B5CFF]" /> {c.modules.length}-part cohort · {c.schedule_kind}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">{c.title}</h1>
          <p className="mt-5 text-[#4A4D57] text-lg leading-relaxed max-w-xl">{c.description}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => setOpen(true)} className="vp-btn-primary">
              Enroll now
            </button>
            <div className="flex items-center gap-2 text-sm text-[#747680]">
              <Users className="w-4 h-4" strokeWidth={1.5} /> {c.enrollments_count} enrolled
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          {c.cover_image ? (
            <img src={c.cover_image} alt="cover" className="w-full aspect-[4/3] rounded-2xl object-cover" />
          ) : (
            <div className="w-full aspect-[4/3] rounded-2xl bg-gradient-to-br from-[#0B5CFF]/10 to-[#0B5CFF]/25 flex items-center justify-center">
              <Layers className="w-16 h-16 text-[#0B5CFF]/60" strokeWidth={1.25} />
            </div>
          )}
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
        <div className="vp-label mb-4">What you'll learn — module by module</div>
        <div className="space-y-3">
          {c.modules.map((m, i) => (
            <div key={m.id} className="vp-card p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#0B5CFF]/10 flex items-center justify-center font-mono text-[#0B5CFF] font-bold">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-lg">{m.title}</div>
                <div className="text-xs text-[#747680] mt-1 inline-flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" /> {c.schedule_kind === "drip" ? "Unlocks" : "Live"} · {new Date(m.scheduled_at).toLocaleString("en-IN")}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#E4E6EC] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{done ? "You're enrolled." : "Enroll in this cohort"}</DialogTitle>
            <DialogDescription className="text-[#4A4D57]">
              {done ? "Check email + WhatsApp — you'll get a portal link for every module." : "One enrollment auto-registers you for every module."}
            </DialogDescription>
          </DialogHeader>
          {!done ? <EnrollForm slug={slug} onDone={setDone} /> : (
            <a href={`/course-portal/${done.portal_token}`} className="vp-btn-primary w-full text-center inline-block" data-testid="course-open-portal">Open my course portal</a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EnrollForm({ slug, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [wa, setWa] = useState("");
  const [country, setCountry] = useState("+91");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/courses/${slug}/enroll`, {
        name, email, whatsapp: wa, country_code: country,
      });
      onDone(data);
      toast.success("You're in!");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-2">
      <div>
        <div className="vp-label mb-1.5">Full name</div>
        <input required className="vp-input" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <div className="vp-label mb-1.5">Email</div>
        <input required type="email" className="vp-input" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div>
        <div className="vp-label mb-1.5">WhatsApp</div>
        <div className="flex gap-2">
          <input className="vp-input w-24" value={country} onChange={(e) => setCountry(e.target.value)} />
          <input required className="vp-input flex-1" value={wa} onChange={(e) => setWa(e.target.value)} placeholder="10-digit number" />
        </div>
      </div>
      <button type="submit" disabled={busy} className="vp-btn-primary w-full" data-testid={TIDS.courseEnrollSubmit}>
        {busy ? "Enrolling…" : "Enroll now"}
      </button>
    </form>
  );
}
